/**
 * Sesión de preview de pago: `pre-initiate` (routing + cache) vs `installment-promo/sync` (solo cuota).
 */
import type { InstallmentPlan } from '../components/InstallmentSelector';
import type { PreInitiateResponse } from '../domain/models';
import type { PaymentMiddlewareClient } from '../infrastructure/paymentMiddlewareClient';
import { toInstallmentPlans } from './installments';
import type { CardChargeWithInstallments } from './processPayment';
import { buildPreInitiateRequest } from './processPayment';
import { parseHostDiscountSummary, type HostDiscountSummary } from './discountSummary';

export type PreChargeWithSlot = CardChargeWithInstallments & { slot: 'first' | 'second' };

export const structuralChargesKey = (preCharges: PreChargeWithSlot[]): string =>
  preCharges
    .map(({ card, amount, slot }) => `${slot}:${card.bin}:${card.lastFourDigits}:${amount}`)
    .join('|');

export const installmentsSelectionKey = (
  selectedInstallmentId: string | undefined,
  secondInstallmentId: string | undefined,
): string => `${selectedInstallmentId ?? ''}|${secondInstallmentId ?? ''}`;

export const runPreInitiate = (
  client: PaymentMiddlewareClient,
  preCharges: PreChargeWithSlot[],
): Promise<PreInitiateResponse> => client.preInitiate(buildPreInitiateRequest(preCharges));

/** Routing + cache del back: siempre 1 cuota en el body; el tag/cuota real se alinea con `installment-promo/sync`. */
export const withDefaultPreviewInstallments = (
  preCharges: PreChargeWithSlot[],
): PreChargeWithSlot[] => preCharges.map((charge) => ({ ...charge, installments: 1 }));

export const runPreInitiateRouting = (
  client: PaymentMiddlewareClient,
  preCharges: PreChargeWithSlot[],
): Promise<PreInitiateResponse> =>
  client.preInitiate(buildPreInitiateRequest(withDefaultPreviewInstallments(preCharges)));

export const resolveInstallmentsSelectionKey = (
  firstPlans: InstallmentPlan[],
  secondPlans: InstallmentPlan[],
  currentFirst?: string,
  currentSecond?: string,
): string => {
  const pick = (plans: InstallmentPlan[], current?: string): string | undefined =>
    plans.find((plan) => plan.id === current)?.id ?? plans.find((plan) => plan.months === 1)?.id;

  return installmentsSelectionKey(pick(firstPlans, currentFirst), pick(secondPlans, currentSecond));
};

export const installmentCountsFromPreCharges = (preCharges: PreChargeWithSlot[]): number[] =>
  preCharges.map(({ installments }) => installments);

export const runInstallmentPromoSync = (
  client: PaymentMiddlewareClient,
  preCharges: PreChargeWithSlot[],
): Promise<PreInitiateResponse> =>
  client.syncInstallmentPromo({ installments: preCharges.map(({ installments }) => installments) });

export type MappedPreview = {
  firstPlans: InstallmentPlan[];
  secondPlans: InstallmentPlan[];
  missingPlans: boolean;
  backendTotal: number | undefined;
  totals: PreInitiateResponse['totals'];
  discountSummary: HostDiscountSummary | undefined;
};

export const mapPreviewResponse = (
  response: PreInitiateResponse,
  preCharges: PreChargeWithSlot[],
): MappedPreview => {
  const plansFor = (slot: 'first' | 'second'): InstallmentPlan[] => {
    const index = preCharges.findIndex((charge) => charge.slot === slot);
    if (index < 0) {
      return [];
    }
    return toInstallmentPlans(response.payments[index], preCharges[index].amount);
  };
  const firstPlans = plansFor('first');
  const secondPlans = plansFor('second');
  const missingPlans = preCharges.some(
    ({ slot }) => (slot === 'first' ? firstPlans : secondPlans).length === 0,
  );
  const backendTotal = response.totals?.total;
  return {
    firstPlans,
    secondPlans,
    missingPlans,
    backendTotal:
      typeof backendTotal === 'number' && backendTotal > 0 ? backendTotal : undefined,
    totals: response.totals,
    discountSummary: parseHostDiscountSummary(response.discountSummary),
  };
};

/**
 * `installment-promo/sync` solo re-simula la cuota elegida en el back (`withPromoPlan`); la fila de 1 cuota
 * debe seguir mostrando el monto con promo del `pre-initiate`. Fusionamos solo la fila seleccionada.
 */
export const mergeInstallmentPlansForPromoSync = (
  routingPlans: InstallmentPlan[],
  syncPlans: InstallmentPlan[],
  selectedInstallmentId: string | undefined,
): InstallmentPlan[] => {
  if (!routingPlans.length) {
    return syncPlans;
  }
  if (!syncPlans.length || !selectedInstallmentId) {
    return routingPlans;
  }
  const selectedMonths =
    routingPlans.find((plan) => plan.id === selectedInstallmentId)?.months ??
    syncPlans.find((plan) => plan.id === selectedInstallmentId)?.months;
  if (!selectedMonths) {
    return routingPlans;
  }
  const syncRow = syncPlans.find((plan) => plan.months === selectedMonths);
  if (!syncRow) {
    return routingPlans;
  }
  return routingPlans.map((plan) => (plan.months === selectedMonths ? syncRow : plan));
};
